import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { contractsProviderData, getContractPaymentsAsync, getProviderData, getQuotationList, getRefundInfo } from "src/api/contract/contract-api";
import { ContractModel, ContractPaymentModel, MemberContractModel, Quotation, ScheduleRecentBill } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { useToast } from "src/pages/hooks/toast";
import { CashReceiptSection } from "./components/paymentDetails/CashReceiptSection";
import { PaymentActionButton } from "./components/paymentDetails/PaymentActionButton";
import MeansOfProof from "./components/paymentDetails/MeansOfProof";
import ServiceTypeGuide from "./components/ServiceTypeGuide";
import ServiceTypePaymentMethodSelector from "./components/ServiceTypePaymentMethodSelector";
import ServiceTypePaymentPriceInfo from "./components/ServiceTypePaymentPriceInfo";
import { checkProgressStatus } from "./components/utils";
import { YmdFormat } from "src/utils/common-util";

type Props = {
  contract: ContractModel;
  contracts: MemberContractModel[] | null;
  recentBill: ScheduleRecentBill;
  paymentStatus: any;
  primaryQuotationName?: any;
  documentProofRequest: any;
  setDocumentProofRequest: any;
  billingContractBill: any;
  handleClickPaymentS2: (pgcode: string, documentProofRequest: any) => void;
  handleClickExpenseProofCashBill: (documentProofRequest: any) => void;
  proofError: any;
  setProofError: any;
};

// 서비스 유형별 결제 정보를 표시하는 컴포넌트
const ServiceTypePaymentInfo = ({
  contract,
  contracts,
  paymentStatus,
  recentBill,
  primaryQuotationName,
  handleClickPaymentS2,
  documentProofRequest,
  setDocumentProofRequest,
  billingContractBill,
  handleClickExpenseProofCashBill,
  proofError,
  setProofError,
}: Props) => {
  const navigate = usePartnerNavigate();
  const { contractId } = useParams();
  const { t } = useTranslation("contract");
  // 현금영수증 관련 에러 상태 관리
  // const [cashErrorTextFlag1, setCashErrorTextFlag1] = useState(false);
  // const [cashErrorTextFlag2, setCashErrorTextFlag2] = useState(false);
  // const [cashErrorTextFlag3, setCashErrorTextFlag3] = useState(false);
  const { openToast } = useToast();
  // 계약 견적서 조회 api
  const { executeAsync: patchContractQuotations } = useApiOperation(getQuotationList);
  const { executeAsync: findSnapshot } = useApiOperation(getRefundInfo);
  const { executeAsync: findProviderData } = useApiOperation(contractsProviderData);
  const { executeAsync: findSellerData } = useApiOperation(getProviderData);
  const { executeAsync: getContractPayments } = useApiOperation(getContractPaymentsAsync);

  //S2 결제 수단 선택 대상 적용 - 기본값 카드 결제
  const [pgcode, setPgcode] = useState<string>(recentBill.pgcode);
  const [phoneNumber, setPhoneNumber] = useState<string>("050-3220-6515");
  const [paymentAgree, setPaymentAgree] = useState(false);
  const [identityNumError, setIdentityNumError] = useState<string>("");

  // 온라인 결제 불가 상품의 공용공간 요금 정보
  const { isNotFree, isOfflinePaymentFree } = useMemo(() => {
    if (contract?.isOnlinePayment === false) {
      const rentalSchedule = contract?.scheduleList?.find((schedule) => schedule.supplyType === "RENTAL");
      const totalAmount = rentalSchedule?.recentBill?.totalAmount || 0;
      console.log("isOnlinePayment:", contract?.isOnlinePayment, "totalAmount:", totalAmount);
      return {
        isNotFree: totalAmount > 0, // 온라인 결제 불가 + 유료
        isOfflinePaymentFree: totalAmount === 0, // 온라인 결제 불가 + 무료
      };
    }
    return { isNotFree: false, isOfflinePaymentFree: false };
  }, [contract]);

  // UI 상태 관리
  const [isAsk, setIsAsk] = useState(false); // 문의하기 모달 표시 여부
  const [isMobile, setIsMobile] = useState(false); // 모바일 기기 여부

  // 데이터 상태 관리
  const [quotation, setQuotation] = useState<any>({}); // 견적서 정보
  const [sellerData, setSellerData] = useState<any>({}); // 판매자 정보
  const [receiptBundle, setReceiptBundle] = useState<any>({}); // 영수증 번들 정보
  const [cashBillList, setCashBillList] = useState<any>([]); // 현금영수증 목록
  const [taxInvoiceList, setTaxInvoiceList] = useState<any>([]); // 세금계산서 목록
  const [refundInfo, setRefundInfo] = useState<any>(null); // 환불 정보
  const [guideInfo, setGuideInfo] = useState<any>(null); // 가이드 정보
  const [contractPayments, setContractPayments] = useState<Array<ContractPaymentModel>>([]); // 계약 결제내역 목록

  //계약 상품의 환불정보 조회
  const fetchFindInfo = useCallback(
    async (contractId: string) => {
      const _guideInfo = await findSnapshot({ contractId: contractId, domain: "product", attrName: "productNotice" } as any);
      if (_guideInfo.status === 200 && _guideInfo.data.data && _guideInfo.data.data.content && _guideInfo.data.data.content.length > 0) {
        setGuideInfo(_guideInfo.data.data.content[0]);
      }
      const _refundInfo = await findSnapshot({ contractId: contractId, domain: "product", attrName: "refundInfo" } as any);
      if (_refundInfo.status === 200 && _refundInfo.data.data && _refundInfo.data.data.content && _refundInfo.data.data.content.length > 0) {
        setRefundInfo(_refundInfo.data.data.content[0]);
      }
    },
    [findSnapshot],
  );

  // 결제정보 상세정보 조회
  const fetchGetContractPayments = useCallback(
    async (contractId: string) => {
      const { data } = await getContractPayments({ contractId });
      setContractPayments(data?.data?.content || []);
    },
    [getContractPayments],
  );

  // 계약 견적서 조회
  const contractQuotationList = useCallback(
    async (quotationList: any) => {
      // console.log("quotationList", quotationList);
      //S2 계약의 견적서 id 정리
      let ids: string = "";
      let orderNumSorted = _.sortBy(quotationList, ["orderNum"]).reverse();

      orderNumSorted!.forEach((list: Quotation) => {
        if (ids !== "") {
          ids = ids + "," + list.quotationNo;
        } else {
          ids = list.quotationNo;
        }
      });

      const response: any = await patchContractQuotations({ quotationNo: ids });

      setQuotation(response.data.data.content[0].quotationItemList[0]);
      //견적서의 공용공간으로 건물의 공용공간 중 매핑되는 것을 처리
      // console.log("response", response.data.data.content[0].quotationItemList[0]);
      //
    },
    [patchContractQuotations],
  );

  // 판매자 정보 조회
  const getSeller = useCallback(
    async (providerId: string) => {
      const seller: any = await findSellerData({ providerId });
      if (seller) {
        setSellerData(seller.data.data);
      }
    },
    [findSellerData],
  );

  // CS 담당자 정보 조회
  const findProviderCs = useCallback(
    async (contractId: string) => {
      // TODO
      const csData: any = await findProviderData({ contractId, assignedWorkType: "CS_PROVIDE" });

      if (csData.data.data.content.length > 0) {
        await getSeller(csData.data.data.content[0].providerId);
        setPhoneNumber(csData.data.data.content[0].contact!);
      }
    },
    [findProviderData, getSeller],
  );

  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    fetchGetContractPayments(String(contract.contractId));

    contractQuotationList(contract.quotationList);
    // console.log("billingContractBill", billingContractBill);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    // const isMobile = true;
    if (isMobile) {
      // mobile
      setIsMobile(true);
    } else {
      // desktop
      setIsMobile(false);
    }
    if (contract.contractId) {
      findProviderCs(String(contract.contractId));
      fetchFindInfo(String(contract.contractId));
    }
    // console.log("billingContractBill", billingContractBill);
    if (billingContractBill) {
      const bundle = billingContractBill.billList[0].originBill.receiptBundle;
      setReceiptBundle(bundle || null);

      setCashBillList(billingContractBill.billList[0].originBill.cashbillList || []);
      setTaxInvoiceList(billingContractBill.billList[0].originBill.taxInvoiceList || []);
    }
  }, [contract, findProviderCs, billingContractBill, setReceiptBundle, contractQuotationList, fetchFindInfo, fetchGetContractPayments]);

  // 만료되지 않은 결제 정보 필터링
  const checkExpireFiltered = useMemo(() => {
    const today = moment();
    let showArr: any = [];

    if (paymentStatus.length === 0 && recentBill.billPayStatus === "PAYMENT_READY") {
      paymentStatus.forEach((status: any) => {
        if (status.virtualAccount !== null) {
          let limit = status.virtualAccount.expireDate + " " + status.virtualAccount.expireTime;
          const limitDate = moment(limit, "YYYYMMDD HHmm");
          if (moment(today).isSameOrBefore(limitDate)) {
            showArr.push(status);
          }
        }
      });
    } else {
      showArr = paymentStatus;
    }

    return showArr;
  }, [paymentStatus, recentBill]);
  // 계좌번호 복사 핸들러
  const onCopyAccount = useCallback(
    async (account: string) => {
      try {
        await navigator.clipboard.writeText(account);
        openToast({ content: t("detail.accountInfoCopied") });
      } catch (err) {
        openToast({ content: t("detail.errorOccurred") });
      }
    },
    [openToast, t],
  );

  // 현재 결제 정보 설정
  let payment: any = {};
  if (!!payment && checkExpireFiltered.length > 0) {
    payment = checkExpireFiltered[0];
  }

  if (!!payment && checkExpireFiltered.length === 0) {
    payment = recentBill;
  }

  // 화면에 표시 중인 결제의 취소 목록
  // 페이레터 주문번호(orderNo)는 신청번호 형식에서 c{계약ID}s{스케줄ID}b{청구ID}p{결제ID} 형식으로 변경되어 신청번호와 직접 비교할 수 없다.
  // paymentId > billId > (구형식) orderNo === 신청번호 순으로 매칭한다.
  const contractCancelList = useMemo(() => {
    if (contractPayments.length === 0) return [];
    const displayedPaymentId = checkExpireFiltered[0]?.paymentId;
    const matched =
      (displayedPaymentId && contractPayments.find((cp) => String(cp.paymentId) === String(displayedPaymentId))) ||
      (recentBill?.billId && contractPayments.find((cp) => String(cp.billId) === String(recentBill.billId))) ||
      contractPayments.find((cp) => String(cp.orderNo) === String(contract?.contractApplyNumber));
    return matched?.cancelList || [];
  }, [contractPayments, checkExpireFiltered, recentBill, contract]);

  const isRequest = (documentProofRequest as any)?.isRequired ?? (documentProofRequest as any)?.isRequest ?? false;
  return (
    <>
      {/* 결제 금액 정보 섹션 */}
      <section className="base-section-layout top-divider">
        <div className="base-section-layout__container">
          <div className="base-section-layout__wrap mb30 pb0">
            <ServiceTypePaymentPriceInfo
              recentBill={recentBill}
              documentProofRequest={documentProofRequest}
              checkExpireFiltered={checkExpireFiltered}
              receiptBundle={receiptBundle}
              onCopyAccount={onCopyAccount}
              contractCancelList={contractCancelList}
            />
          </div>
        </div>
      </section>

      {/* 지출증빙 섹션 */}
      {contract.contractStep !== "APPLY_CANCEL" &&
        checkExpireFiltered.length > 0 &&
        recentBill?.pgcode === "virtualaccount" &&
        documentProofRequest.expenseProofType === "CASHBILL" && <MeansOfProof documentProofRequest={documentProofRequest} />}

      {/* 현금영수증 섹션 */}
      {isRequest &&
        contract.contractStep !== "APPLY_CANCEL" &&
        checkExpireFiltered.length > 0 &&
        recentBill?.pgcode === "virtualaccount" &&
        documentProofRequest.expenseProofType === "CASHBILL" && (
          <CashReceiptSection
            recentBill={recentBill}
            documentProofRequest={documentProofRequest}
            cashBillList={cashBillList}
            paymentAgree={paymentAgree}
            setPaymentAgree={setPaymentAgree}
            proofError={proofError}
            setDocumentProofRequest={setDocumentProofRequest}
            setCashErrorFlags={(flag1, flag2, flag3) => {
              // setCashErrorTextFlag1(flag1);
              // setCashErrorTextFlag2(flag2);
              // setCashErrorTextFlag3(flag3);
            }}
            handleClickExpenseProofCashBill={handleClickExpenseProofCashBill}
          />
        )}

      {/* 세금계산서 섹션 */}
      {contract.contractStep !== "APPLY_CANCEL" &&
        checkExpireFiltered.length > 0 &&
        recentBill?.pgcode === "virtualaccount" &&
        documentProofRequest.expenseProofType !== "CASHBILL" && (
          <>
            <section className="base-section-layout top-divider">
              <div className="base-section-layout__container">
                <div className="base-section-layout__wrap mb30 pb0">
                  <div className="base-section-layout__title-wrap">
                    <h2>{t("detail.taxInvoice")}</h2>
                  </div>
                  {taxInvoiceList.length === 0 && (
                    <div className="base-section-layout-line">
                      <p className="base-section-layout-index text-primary3">{t("detail.issuance")}</p>
                      <p className="base-section-layout-value">{t("detail.issuancePending")}</p>
                    </div>
                  )}
                  {taxInvoiceList.length > 0 && (
                    <div className="base-section-layout-line">
                      <p className="base-section-layout-index text-primary3">{t("detail.issuance")}</p>
                      {taxInvoiceList.map((tax: any, index: number) => {
                        console.log("tax", tax);
                        return (
                          <p className="base-section-layout-value" key={index}>
                            {checkProgressStatus(tax.progressStatus)}
                          </p>
                        );
                      })}
                    </div>
                  )}

                  <article>
                    <section className="tax-calculate">
                      <div className="container">
                        <div className="base-section-layout-line">
                          <p className="base-section-layout-index text-primary3">{t("detail.businessRegistrationNumber")}</p>
                          <p className="base-section-layout-value">{documentProofRequest.identityNum}</p>
                        </div>
                        <div className="base-section-layout-line">
                          <p className="base-section-layout-index text-primary3">{t("detail.recipientEmail")}</p>
                          <p className="base-section-layout-value">{documentProofRequest.taxInvoiceEmail}</p>
                        </div>
                        <div className="base-section-layout-line">
                          <p className="base-section-layout-index text-primary3">{t("detail.landlineNumber")}</p>
                          <p className="base-section-layout-value">{documentProofRequest.taxInvoicePhone}</p>
                        </div>
                        <div className="base-section-layout-line">
                          <p className="base-section-layout-index text-primary3">{t("detail.taxInvoiceIssueDate")}</p>
                          <p className="base-section-layout-value">
                            {documentProofRequest.taxInvoiceDate && moment(documentProofRequest.taxInvoiceDate).format("YYYY-MM-DD")}
                          </p>
                        </div>
                      </div>
                      {(taxInvoiceList.length === 0 ||
                        taxInvoiceList[0].progressStatus === "TRANS_READY" ||
                        taxInvoiceList[0].progressStatus === "TEMP_SAVE") && (
                          <>
                            <div className="base-floating-btn-wrap px0">
                              <button
                                type="button"
                                className="base-btn color-white base-btn-cashbill"
                                onClick={() => {
                                  navigate(`/court/mypage/contracts/${contractId}/TaxInvoiceInfoEdit`);
                                }}
                              >
                                {t("detail.editIssuanceInformation")}
                              </button>
                            </div>
                          </>
                        )}
                    </section>
                  </article>
                </div>
              </div>
            </section>
          </>
        )}

      {/* 결제 수단 선택 섹션 */}
      {checkExpireFiltered.length === 0 &&
        ["CONTRACT_ACCEPT", "USE_APPROVAL"].includes(contract?.contractStep!) &&
        recentBill?.billPayStatus === "PAYMENT_READY" && (
          <ServiceTypePaymentMethodSelector
            contracts={contracts}
            contract={contract}
            recentBill={recentBill}
            currentPgCode={pgcode}
            newPgCode={setPgcode}
            documentProofRequest={documentProofRequest}
            setDocumentProofRequest={setDocumentProofRequest}
            proofError={proofError}
            setProofError={setProofError}
            identityNumError={identityNumError}
            setIdentityNumError={setIdentityNumError}
            isNotFree={isNotFree}
          />
        )}

      {/* 0원 무료상품 이후 결제 액션 버튼 섹션 */}
      {recentBill?.billPayStatus === "PAYMENT_FREE" && ["CONTRACT_ACCEPT"].includes(contract?.contractStep!) && (
        <>
          {/* 비활성화 처리 > UI만 보여주는 용도 */}
          <ServiceTypePaymentMethodSelector
            contracts={contracts}
            contract={contract}
            recentBill={recentBill}
            currentPgCode={pgcode}
            newPgCode={setPgcode}
            documentProofRequest={documentProofRequest}
            setDocumentProofRequest={setDocumentProofRequest}
            proofError={proofError}
            setProofError={setProofError}
            isFreeDisabled={true}
            identityNumError={identityNumError}
            setIdentityNumError={setIdentityNumError}
          />
          <ServiceTypeGuide
            guideInfo={guideInfo}
            refundInfo={refundInfo}
            sellerData={sellerData}
            contractStatus={contract?.contractStep!}
            payment={payment}
          />
          <section className={`base-section-layout ${recentBill?.isVerifiedPaymentKey === true ? "top-divider" : ""}`}>
            <PaymentActionButton
              isVerifiedPaymentKey={recentBill.isVerifiedPaymentKey}
              isMobile={isMobile}
              isAsk={isAsk}
              contract={contract}
              phoneNumber={phoneNumber}
              primaryQuotationName={primaryQuotationName}
              pgcode={pgcode}
              documentProofRequest={documentProofRequest}
              handleClickPaymentS2={handleClickPaymentS2}
              setIsAsk={setIsAsk}
              setIdentityNumError={setIdentityNumError}
              paymentDisabled={isNotFree}
            />
          </section>
        </>
      )}

      {/* 결제 액션 버튼 섹션 */}
      {checkExpireFiltered.length === 0 &&
        recentBill?.billPayStatus === "PAYMENT_READY" &&
        ["CONTRACT_ACCEPT", "USE_APPROVAL"].includes(contract?.contractStep!) ? (
        <>
          {/* 서비스 가이드 섹션 */}
          <ServiceTypeGuide
            guideInfo={guideInfo}
            refundInfo={refundInfo}
            sellerData={sellerData}
            contractStatus={contract?.contractStep!}
            payment={payment}
          />
          <section className={`base-section-layout ${recentBill?.isVerifiedPaymentKey === true ? "top-divider" : ""}`}>
            <PaymentActionButton
              isVerifiedPaymentKey={recentBill.isVerifiedPaymentKey}
              isMobile={isMobile}
              isAsk={isAsk}
              contract={contract}
              phoneNumber={phoneNumber}
              primaryQuotationName={primaryQuotationName}
              pgcode={pgcode}
              documentProofRequest={documentProofRequest}
              handleClickPaymentS2={handleClickPaymentS2}
              setIsAsk={setIsAsk}
              setIdentityNumError={setIdentityNumError}
              paymentDisabled={isNotFree}
            />
          </section>
        </>
      ) : (
        <section className={recentBill?.billPayStatus === "PAYMENT_FREE" ? "" : "base-section-layout top-divider"} />
      )}
    </>
  );
};
export default ServiceTypePaymentInfo;
